import { useCallback, useEffect, useState } from "react";

/**
 * What the breeder chose, which is not the same as what they see. "System" is a
 * preference to keep following the device, not a third appearance.
 */
export type ThemePreference = "system" | "light" | "dark";

/** What is actually on screen once "system" has been resolved. */
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "breenova.theme";

/**
 * What a breeder gets before they have chosen anything.
 *
 * Light rather than "system", deliberately. This app is looked at in daylight, next to
 * paper records and ring boxes, and the sage palette was drawn for that; a phone left on
 * its own night schedule would otherwise hand a first-time breeder the dark skin and let
 * them conclude that is what BreeNova looks like. "System" is still there in Appearance
 * for anyone who wants their device to decide — it is just no longer decided for them.
 */
const DEFAULT_PREFERENCE: ThemePreference = "light";

/**
 * Matches the token layer: `tokens.css` defines the dark roles under `.dark`, so this
 * class on the root element is the whole switch. No component has a dark-specific branch.
 */
const DARK_CLASS = "dark";

function isPreference(value: unknown): value is ThemePreference {
  return value === "system" || value === "light" || value === "dark";
}

/**
 * The stored preference, defaulting to light.
 *
 * Reads defensively: Safari in private mode throws on `localStorage` rather than
 * returning null, and a theme is not worth a blank screen.
 */
export function readPreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);

    return isPreference(stored) ? stored : DEFAULT_PREFERENCE;
  } catch {
    return DEFAULT_PREFERENCE;
  }
}

function writePreference(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // Storage refused. The choice still applies for this session, which is better than
    // failing the click.
  }
}

function systemPrefersDark(): boolean {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === "system") {
    return systemPrefersDark() ? "dark" : "light";
  }

  return preference;
}

/**
 * Puts the resolved theme on the document.
 *
 * Exported because `index.html` runs the same logic inline before React mounts. Without
 * that, a breeder who picked dark gets a white flash on every load — which in a dim
 * birdroom at six in the morning is the one thing dark mode existed to prevent.
 */
export function applyTheme(theme: ResolvedTheme): void {
  document.documentElement.classList.toggle(DARK_CLASS, theme === "dark");
  document.documentElement.style.colorScheme = theme;

  // Read back off the token rather than restated as a literal here. The mobile browser
  // chrome then tracks `--color-surface` on its own, and cannot drift from it.
  const surface = getComputedStyle(document.documentElement)
    .getPropertyValue("--color-surface")
    .trim();

  if (surface) {
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", surface);
  }
}

/**
 * The appearance setting: the breeder's choice, what it currently resolves to, and a
 * setter that persists.
 *
 * While the preference is "system" the hook keeps listening, so a phone crossing into
 * its own night mode takes BreeNova with it without the app being reopened.
 */
export function useTheme(): {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (next: ThemePreference) => void;
} {
  const [preference, setStoredPreference] = useState<ThemePreference>(readPreference);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(readPreference()));

  useEffect(() => {
    const next = resolveTheme(preference);
    setResolved(next);
    applyTheme(next);

    if (preference !== "system") {
      return;
    }

    const query = window.matchMedia("(prefers-color-scheme: dark)");

    const followDevice = (): void => {
      const followed = systemPrefersDark() ? "dark" : "light";
      setResolved(followed);
      applyTheme(followed);
    };

    query.addEventListener("change", followDevice);

    return () => query.removeEventListener("change", followDevice);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference): void => {
    writePreference(next);
    setStoredPreference(next);
  }, []);

  return { preference, resolved, setPreference };
}
